import { createRouter, createWebHistory, START_LOCATION } from "vue-router";
import iframeRoutes from "@/assets/iframeRoutes";
import { useUserStore } from "@/stores/userStore";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    {
      path: "/",
      name: "UserDashboard",
      component: () => import("@/views/DashboardView.vue"),
    },
    {
      path: "/user/tenant_id/:tenantId",
      name: "VerboseUserDashboard",
      component: () => import("@/views/DashboardView.vue"),
    },
    {
      path: "/review-batch",
      name: "ExistingBatch",
      component: () => import("@/views/ExistingBatchView.vue"),
    },
    {
      path: "/payment-sync",
      name: "NewPaymentSync",
      component: () => import("@/views/PaymentSyncView.vue"),
    },
    {
      path: "/manage-customers",
      name: "ManageCustomers",
      component: () => import("@/views/ManageCustomersView.vue"),
    },
    {
      path: "/manage-users",
      name: "ManageMobiusUsers",
      component: () => import("@/views/ManageMobiusUsersView.vue"),
    },
    {
      path: "/invoices",
      name: "ManageInvoices",
      component: () => import("@/views/ManageInvoicesView.vue"),
    },
    {
      path: "/contracts",
      name: "ManageContracts",
      component: () => import("@/views/ManageContractsView.vue"),
    },
    {
      path: "/products",
      name: "ManageProducts",
      component: () => import("@/views/ManageProductsView.vue"),
    },
    {
      path: "/customer/new",
      name: "CustomerNew",
      component: () => import("@/views/CustomerView.vue"),
      meta: { mode: "new" },
    },
    {
      path: "/customer/:id",
      name: "CustomerDetail",
      component: () => import("@/views/CustomerView.vue"),
    },
    {
      path: "/product/new",
      name: "ProductNew",
      component: () => import("@/views/ProductView.vue"),
      meta: { mode: "new" },
    },
    {
      path: "/product/:id",
      name: "ProductDetail",
      component: () => import("@/views/ProductView.vue"),
    },
    {
      path: "/contract/new",
      name: "ContractNew",
      component: () => import("@/views/ContractView.vue"),
      meta: { mode: "new" },
    },
    {
      path: "/contract/:id",
      name: "ContractDetail",
      component: () => import("@/views/ContractView.vue"),
    },
    {
      path: "/invoice/new",
      name: "InvoiceNew",
      component: () => import("@/views/InvoiceView.vue"),
      meta: { mode: "new" },
    },
    {
      path: "/invoice/:id",
      name: "InvoiceDetail",
      component: () => import("@/views/InvoiceView.vue"),
    },
    {
      path: "/gozynta-payments",
      name: "GozyntaPayments",
      component: () => import("@/views/GozyntaPaymentsView.vue"),
    },
    {
      path: "/customer-portal",
      name: "CustomerPortal",
      component: () => import("@/views/CustomerPortalView.vue"),
    },
    {
      path: "/billing",
      name: "BillingSettings",
      component: () => import("@/views/BillingView.vue"),
    },
    {
      path: "/accounting-configurations",
      name: "AccountingConfig",
      component: () => import("@/views/AccountingConfigView.vue"),
    },
    {
      path: "/release-notes/:version",
      name: "ReleaseNotes",
      component: () => import("@/views/ReleaseNotesView.vue")
    },
    {
      path: "/update-plan",
      name: "UpdatePlan",
      component: () => import("@/views/UpdatePlanView.vue")
    },
    {
      path: "/no-tenant",
      name: "NoTenant",
      component: () => import("@/views/NoTenantFoundView.vue")
    },
    // Admin routes
    {
      path: "/admin",
      name: "AdminDashboard",
      redirect: { name: "ManageTenants" },
    },
    {
      path: "/admin/tenants",
      name: "ManageTenants",
      component: () => import("@/views/admin/TenantsView.vue"),
    },
    {
      path: "/admin/tenants/:tenantId",
      name: "ManageTenantById",
      component: () => import("@/views/admin/ManageTenantByIdView.vue"),
    },
    {
      path: "/admin/manage-admin-users",
      name: "ManageAdminUsers",
      component: () => import("@/views/admin/ManageAdminUsersView.vue"),
    },
    // iframe routes & 404
    ...iframeRoutes,
    {
      path: "/:path(.*)",
      name: "NotFound",
      component: () => import("@/views/NotFoundView.vue"),
    },
  ],
});

const routeAccessConfig = {
  // Routes that require QBO plan or higher
  qbo: [
    "iframe-user-create_inventory_batch",
    "GozyntaPayments",
    "CustomerPortal"
  ],
  
  // Routes that require Standard plan
  standard: [
    "ManageCustomers",
    "CustomerDetail",
    "CustomerNew",
    "ManageInvoices",
    "InvoiceDetail",
    "InvoiceNew",
    "ManageContracts",
    "ContractDetail",
    "ContractNew",
    "ManageProducts",
    "ProductDetail",
    "ProductNew"
    // + anything else using the PSA API
    // + new Payments portal setup
  ]
};

function checkRouteAccess(routeName: string, planType: string): boolean {
  if (planType === "standard") return true;
  
  if (planType === "qbo") {
    return !routeAccessConfig.standard.includes(routeName);
  }
  
  if (planType === "free" || planType === "lite") {
    return !routeAccessConfig.qbo.includes(routeName) && 
           !routeAccessConfig.standard.includes(routeName);
  }
  
  return true;
}

router.beforeEach(async (to, from, next) => {
  const userStore = useUserStore();

  // Hold the layout behind a loading screen when crossing the /admin boundary, so the
  // sidebar / banner / dropdown don't reactively switch modes while the new route's
  // lazy chunk is still being fetched (afterEach fires once the chunk resolves).
  if (from.path.startsWith("/admin") !== to.path.startsWith("/admin")) {
    userStore.isNavigating = true;
  }

  if (userStore.storeState !== "ready") {
    // Keep the loading screen up while the guard initialises user data
    userStore.isNavigating = true;
    try {
      await userStore.initUserData();
    } catch (error) {
      console.error("Error initializing user data:", error);
    }
  }
  
  // Redirect users without any tenants to NoTenant page (unless they have admin access)
  if (!userStore.hasAdminAccess && userStore.tenants.length === 0 && to.name !== "NoTenant") {
    next({ name: "NoTenant" });
    return;
  }

  // Prevent users without admin access from reaching admin routes
  if (!userStore.hasAdminAccess && to.path.startsWith("/admin")) {
    next("/");
    return;
  }

  if (to.path.startsWith("/admin") && userStore.currentTenant) {
    userStore.currentTenant = null;
  }

  // Auto-select tenant when arriving from a cross-version redirect (e.g. v3.0 → v2.1).
  const redirectTenantId = to.query.tenantId ? Number(to.query.tenantId) : null;
  if (redirectTenantId && !userStore.currentTenant && !to.path.startsWith("/admin")) {
    const tenant = userStore.tenants.find(t => t.id === redirectTenantId);
    if (tenant) {
      const redirected = userStore.setCurrentTenant(tenant, to.path);
      if (redirected) return; // tenantId is already in the query
    } else {
      // tenantId doesn't match any of the user's tenants so strip it to show tenant picker
      const cleanQuery = { ...to.query };
      delete cleanQuery.tenantId;
      next({ path: to.path, query: cleanQuery, hash: to.hash, replace: true });
      return;
    }
  }

  // Auto-select for single-tenant users on non-admin routes
  if (!userStore.currentTenant && userStore.tenants.length === 1 && !to.path.startsWith("/admin")) {
    const redirected = userStore.setCurrentTenant(userStore.tenants[0], to.path);
    if (redirected) return;
  }

  // Admin with no tenant memberships landing on a non-admin page -> send to admin console
  if (userStore.hasAdminAccess && userStore.tenants.length === 0 && !to.path.startsWith("/admin")) {
    next({ name: "AdminDashboard" });
    return;
  }
  
  // Plan-based access control
  const routeName = to.name as string;
  const planType = userStore.getPlanName(userStore.currentTenant?.subscription?.plan_id ?? "");
  
  if (!checkRouteAccess(routeName, planType)) {
    to.meta.requiresUpgrade = true;
  }

  // makes sure tenantId is always present for SPA navigation
  // preserve tenant context on refresh / opening links in new tabs.
  if (!to.path.startsWith("/admin") && userStore.currentTenant) {
    const expectedTenantId = String(userStore.currentTenant.id);
    if (to.query.tenantId !== expectedTenantId) {
      next({
        path: to.path,
        query: { ...to.query, tenantId: expectedTenantId },
        hash: to.hash,
        // Only the first load may replace (avoid nested nav bugs)
        replace: from === START_LOCATION,
      });
      return;
    }
  }
  
  next();
});

router.afterEach(() => {
  const userStore = useUserStore();
  userStore.isNavigating = false;
});

export default router;
